import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { duplicateItemGroups } from '../../../mocks/data/itemRationalisationData';

/** Identify + merge duplicate items. Merge only updates local UI state (marks the group
 * resolved) - no backend persistence exists for item merges yet. */
const DuplicatesTab = () => {
  const { t } = useTranslation('productManagement');
  const [resolvedGroups, setResolvedGroups] = useState<Set<string>>(new Set());

  return (
    <Stack spacing={2}>
      <Typography variant="textSM"
        color="text.secondary">
        {t('rationalisation.duplicatesIntro', 'Items that appear to describe the same product under different item numbers.')}
      </Typography>
      {duplicateItemGroups.map((group) => {
        const resolved = resolvedGroups.has(group.groupId);
        return (
          <Stack key={group.groupId}
            sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
            <Stack direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ mb: 1 }}>
              <Typography variant="textSM"
                fontWeight={600}>
                {t('rationalisation.suggestedPreferred', 'Suggested preferred item')}: {group.suggestedPreferred}
              </Typography>
              {resolved ? (
                <Chip label={t('rationalisation.merged', 'Merged')}
                  color="success"
                  size="small" />
              ) : (
                <Button
                  variant="secondary"
                  size="md"
                  onClick={() => setResolvedGroups((prev) => new Set(prev).add(group.groupId))}
                >
                  {t('rationalisation.mergeItems', 'Merge items')}
                </Button>
              )}
            </Stack>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('itemNumber', 'Item Number')}</TableCell>
                  <TableCell>{t('description', 'Description')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {group.items.map((item) => (
                  <TableRow key={item.itemNumber}>
                    <TableCell>{item.itemNumber}</TableCell>
                    <TableCell>{item.description}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Stack>
        );
      })}
    </Stack>
  );
};

export default DuplicatesTab;
